import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'

// Layouts
import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { AuthLayout } from '@/components/layouts/AuthLayout'

// Auth Pages
import { LoginPage } from '@/pages/auth/LoginPage'
import { RegisterPage } from '@/pages/auth/RegisterPage'

// Dashboard Pages
import { DashboardPage } from '@/pages/dashboard/DashboardPage'
import { DomainsPage } from '@/pages/domains/DomainsPage'
import { DomainDetailPage } from '@/pages/domains/DomainDetailPage'
import { SitesPage } from '@/pages/sites/SitesPage'
import { SiteDetailPage } from '@/pages/sites/SiteDetailPage'
import { GenerateWizardPage } from '@/pages/sites/GenerateWizardPage'
import { AnalyticsPage } from '@/pages/analytics/AnalyticsPage'
import { LeadsPage } from '@/pages/leads/LeadsPage'
import { BatchPage } from '@/pages/batch/BatchPage'
import { SettingsPage } from '@/pages/settings/SettingsPage'
import { BillingPage } from '@/pages/settings/BillingPage'
import { CloudflarePage } from '@/pages/settings/CloudflarePage'
import { TeamPage } from '@/pages/settings/TeamPage'

// Landing Page
import { LandingPage } from '@/pages/LandingPage'

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Public Route (redirect if logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

export default function App() {
  const { token, login, logout, setLoading } = useAuthStore()

  // Check session on mount
  const { isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      if (!token) {
        setLoading(false)
        return null
      }
      try {
        const data = await api.getSession()
        login(data.user, token)
        return data.user
      } catch {
        logout()
        return null
      }
    },
    retry: false,
    staleTime: Infinity,
  })

  useEffect(() => {
    if (!isLoading) {
      setLoading(false)
    }
  }, [isLoading, setLoading])

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Landing */}
        <Route path="/" element={<LandingPage />} />

        {/* Auth Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <AuthLayout>
                <LoginPage />
              </AuthLayout>
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <AuthLayout>
                <RegisterPage />
              </AuthLayout>
            </PublicRoute>
          }
        />

        {/* Dashboard Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="domains" element={<DomainsPage />} />
          <Route path="domains/:id" element={<DomainDetailPage />} />
          <Route path="sites" element={<SitesPage />} />
          <Route path="sites/:id" element={<SiteDetailPage />} />
          <Route path="generate" element={<GenerateWizardPage />} />
          <Route path="generate/:domainId" element={<GenerateWizardPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="batch" element={<BatchPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/billing" element={<BillingPage />} />
          <Route path="settings/cloudflare" element={<CloudflarePage />} />
          <Route path="settings/team" element={<TeamPage />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
